import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import { templateQueries } from "@/domains/templates/queries";
import { variableToInputFieldDefinition } from "@/domains/templates/input-fields";
import type {
  InputFieldDefinition,
  LinkedVariableOption,
  ScopedVariableValueInput,
  VariableValueInput,
} from "@/domains/templates/types";
import type { Json } from "@/types/supabase.types";
import { cn } from "@/shared/lib/utils";
import { useTenant } from "@/domains/tenant/hooks";

type RuntimeVariableInputProps = {
  id: string;
  field: InputFieldDefinition;
  value: string;
  analysisId?: string;
  isMissing?: boolean;
  className?: string;
  childValues?: Record<string, string>;
  linkedAnalyses?: LinkedVariableOption[];
  linkedAnalysisSteps?: LinkedVariableOption[];
  excludedLinkedAnalysisStepIds?: string[];
  linkedAnalysesLoading?: boolean;
  linkedAnalysisStepsLoading?: boolean;
  onChange: (value: string) => void;
  onChildChange?: (field: InputFieldDefinition, value: string) => void;
  onChildrenChange?: (
    field: InputFieldDefinition,
    children: InputFieldDefinition[],
  ) => void;
};

type RuntimeOption = {
  label: string;
  value: string;
};

const EMPTY_CHILD_VALUES: Record<string, string> = {};
const EMPTY_LINKED_OPTIONS: LinkedVariableOption[] = [];
const EMPTY_EXCLUDED_LINKED_ANALYSIS_STEP_IDS: string[] = [];

function optionLabelFromValue(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function parseOptions(raw: Json | undefined): RuntimeOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (typeof item === "string") {
      return [{ value: item, label: optionLabelFromValue(item) }];
    }
    if (!item || typeof item !== "object" || Array.isArray(item)) return [];
    const record = item as Record<string, unknown>;
    const rawValue = record.value ?? record.key ?? record.id;
    if (typeof rawValue !== "string") return [];
    const rawLabel = record.label ?? record.name ?? record.title;
    return [
      {
        value: rawValue,
        label:
          typeof rawLabel === "string"
            ? rawLabel
            : optionLabelFromValue(rawValue),
      },
    ];
  });
}

function jsonToRuntimeText(value: Json | null | undefined): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

export function resolveRuntimeDefaultValue(
  field: InputFieldDefinition,
  tenantValue: Json | undefined,
): string {
  return jsonToRuntimeText(field.defaultValue ?? tenantValue ?? null);
}

export function resolveStepRuntimeDefaultValue(
  field: InputFieldDefinition,
  inheritedAnalysisValue: string | undefined,
  tenantValue: Json | undefined,
): string {
  return (
    inheritedAnalysisValue ?? resolveRuntimeDefaultValue(field, tenantValue)
  );
}

function parseArrayValue(rawValue: string): string[] {
  const trimmed = rawValue.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (item): item is string => typeof item === "string",
        );
      }
    } catch {
      // Fall back to comma-separated text below.
    }
  }
  return trimmed
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function serializeArrayValue(values: string[]) {
  return JSON.stringify(values);
}

function linkedTableForField(field: InputFieldDefinition) {
  return field.variableLinkedTable ?? null;
}

function fieldReference(
  field: InputFieldDefinition,
  value: Json,
): VariableValueInput {
  const displayName = field.variableNameFrontend?.trim() || field.key.trim();
  return {
    ...(field.variableId
      ? { variable_id: field.variableId }
      : field.source === "variable"
        ? { variable_name: field.key }
        : { variable_name_frontend: displayName }),
    value,
  };
}

function shouldOmitBlankRuntimeValue(
  field: InputFieldDefinition,
  rawValue: string,
) {
  return field.required === false && rawValue.trim().length === 0;
}

export function fieldValueKey(field: InputFieldDefinition) {
  return field.variableId ?? field.key;
}

export function resolveStepRuntimeValue(
  stepValues: Record<string, string> | undefined,
  field: InputFieldDefinition,
  inheritedValue: string,
): string {
  const stableKey = fieldValueKey(field);
  if (Object.prototype.hasOwnProperty.call(stepValues ?? {}, stableKey)) {
    return stepValues?.[stableKey] ?? "";
  }
  if (
    stableKey !== field.key &&
    Object.prototype.hasOwnProperty.call(stepValues ?? {}, field.key)
  ) {
    return stepValues?.[field.key] ?? "";
  }
  return inheritedValue;
}

export function setStepRuntimeValue(
  current: Record<string, Record<string, string>>,
  stepId: string,
  field: InputFieldDefinition,
  value: string,
): Record<string, Record<string, string>> {
  return {
    ...current,
    [stepId]: {
      ...(current[stepId] ?? {}),
      [fieldValueKey(field)]: value,
    },
  };
}

export function clearStepRuntimeValue(
  values: Record<string, string>,
  field: InputFieldDefinition,
): Record<string, string> {
  const next = { ...values };
  delete next[fieldValueKey(field)];
  delete next[field.key];
  return next;
}

export function parseRuntimeInputValue(
  field: InputFieldDefinition,
  rawValue: string,
): Json {
  const trimmed = rawValue.trim();
  if (!trimmed) return "";
  if (field.type === "boolean") return trimmed.toLowerCase() === "true";
  if (field.type === "number" || field.type === "integer") {
    const value = Number(trimmed);
    return Number.isNaN(value) ? trimmed : value;
  }
  if (field.type === "array") return parseArrayValue(trimmed);
  if (field.type === "json") {
    try {
      return JSON.parse(trimmed) as Json;
    } catch {
      return trimmed;
    }
  }
  return trimmed;
}

export function shouldShowChildVariables(
  field: InputFieldDefinition,
  rawValue: string,
) {
  if (!field.sourceVariableId && !field.variableId) return false;
  if (field.type === "boolean") return rawValue.trim().toLowerCase() === "true";
  return rawValue.trim().length > 0;
}

export function buildRuntimeVariableValues(
  fields: InputFieldDefinition[],
  childFieldsByParentKey: Map<string, InputFieldDefinition[]>,
  getValue: (field: InputFieldDefinition) => string,
): VariableValueInput[] {
  return fields.flatMap((field) => {
    const key = field.key.trim();
    if (!key) return [];

    const rawValue = getValue(field);
    const parentValue = parseRuntimeInputValue(field, rawValue);
    const values = [fieldReference(field, parentValue)];

    if (!shouldShowChildVariables(field, rawValue)) return values;

    for (const child of childFieldsByParentKey.get(key) ?? []) {
      const childKey = child.key.trim();
      if (!childKey) continue;
      const childRawValue = getValue(child);
      if (shouldOmitBlankRuntimeValue(child, childRawValue)) continue;
      values.push(
        fieldReference(child, parseRuntimeInputValue(child, childRawValue)),
      );
    }

    return values;
  });
}

export function requireScopedVariableValues(
  values: VariableValueInput[],
): ScopedVariableValueInput[] {
  return values.map((value) => {
    if (!value.variable_id) {
      throw new Error("Template input is missing its scoped variable id");
    }
    return { variable_id: value.variable_id, value: value.value };
  });
}

export function runtimeChildFieldsSignature(fields: InputFieldDefinition[]) {
  return fields
    .map(
      (field) =>
        `${field.key}:${field.sourceVariableId ?? field.variableId ?? ""}`,
    )
    .join("|");
}

export function bindScopedChildDefinitions(
  catalogChildren: InputFieldDefinition[],
  scopedFields: InputFieldDefinition[],
): InputFieldDefinition[] {
  const scopedBySourceId = new Map(
    scopedFields.flatMap((field) =>
      field.sourceVariableId ? [[field.sourceVariableId, field] as const] : [],
    ),
  );
  return catalogChildren.map(
    (child) =>
      (child.sourceVariableId
        ? scopedBySourceId.get(child.sourceVariableId)
        : undefined) ?? child,
  );
}

export function filterLinkedAnalysisStepOptions(
  options: LinkedVariableOption[],
  excludedIds: string[],
) {
  if (excludedIds.length === 0) return options;
  const excluded = new Set(excludedIds);
  return options.filter((option) => !excluded.has(option.id));
}

export function getRuntimeChildFieldValue(
  field: InputFieldDefinition,
  childValues: Record<string, string>,
) {
  return (
    childValues[fieldValueKey(field)] ??
    childValues[field.key] ??
    jsonToRuntimeText(field.defaultValue)
  );
}

export function RuntimeVariableInput({
  id,
  field,
  value,
  analysisId,
  isMissing = false,
  className,
  childValues = EMPTY_CHILD_VALUES,
  linkedAnalyses = EMPTY_LINKED_OPTIONS,
  linkedAnalysisSteps = EMPTY_LINKED_OPTIONS,
  excludedLinkedAnalysisStepIds = EMPTY_EXCLUDED_LINKED_ANALYSIS_STEP_IDS,
  linkedAnalysesLoading = false,
  linkedAnalysisStepsLoading = false,
  onChange,
  onChildChange,
  onChildrenChange,
}: RuntimeVariableInputProps) {
  const { data: tenantData } = useTenant();
  const commonClassName = cn(
    className,
    isMissing
      ? "border-destructive focus-visible:ring-destructive/30"
      : undefined,
  );
  const options = useMemo(
    () => parseOptions(field.variableOptions),
    [field.variableOptions],
  );
  const linkedTable = linkedTableForField(field);
  const selectedArrayValues = parseArrayValue(value);
  const filteredLinkedAnalysisSteps = useMemo(
    () =>
      filterLinkedAnalysisStepOptions(
        linkedAnalysisSteps,
        excludedLinkedAnalysisStepIds,
      ),
    [excludedLinkedAnalysisStepIds, linkedAnalysisSteps],
  );
  const linkedOptions =
    linkedTable === "analysis"
      ? linkedAnalyses
      : linkedTable === "analysisstep"
        ? filteredLinkedAnalysisSteps
        : [];
  const linkedOptionsLoading =
    linkedTable === "analysis"
      ? linkedAnalysesLoading
      : linkedAnalysisStepsLoading;

  const catalogVariableId = field.sourceVariableId ?? field.variableId;
  const shouldLoadChildren =
    Boolean(catalogVariableId) && shouldShowChildVariables(field, value);
  const { data: childVariables } = useQuery({
    ...templateQueries.variableChildren(
      catalogVariableId ?? "",
      true,
      tenantData?.tenant.tenant_id,
    ),
    enabled: shouldLoadChildren && Boolean(tenantData?.tenant.tenant_id),
  });
  const childFields = useMemo(
    () => (childVariables ?? []).map(variableToInputFieldDefinition),
    [childVariables],
  );
  const childFieldsSignature = useMemo(
    () => (shouldLoadChildren ? runtimeChildFieldsSignature(childFields) : ""),
    [childFields, shouldLoadChildren],
  );
  const previousChildFieldsSignatureRef = useRef<string | null>(null);
  const onChildrenChangeRef = useRef(onChildrenChange);

  useEffect(() => {
    onChildrenChangeRef.current = onChildrenChange;
  }, [onChildrenChange]);

  useEffect(() => {
    if (previousChildFieldsSignatureRef.current === childFieldsSignature) {
      return;
    }

    previousChildFieldsSignatureRef.current = childFieldsSignature;
    onChildrenChangeRef.current?.(field, shouldLoadChildren ? childFields : []);
  }, [childFields, childFieldsSignature, field, shouldLoadChildren]);

  const input =
    linkedTable === "analysis" || linkedTable === "analysisstep" ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn("w-full justify-between", commonClassName)}
            disabled={linkedOptionsLoading}
            aria-label={field.variableNameFrontend ?? field.key}
          >
            <span className="truncate text-left">
              {selectedArrayValues.length > 0
                ? linkedOptions
                    .filter((option) => selectedArrayValues.includes(option.id))
                    .map((option) => option.name)
                    .join(", ")
                : linkedOptionsLoading
                  ? "Loading..."
                  : "Select references"}
            </span>
            <ChevronDown className="h-4 w-4 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[var(--radix-dropdown-menu-trigger-width)]"
        >
          {linkedOptions.map((option) => {
            const checked = selectedArrayValues.includes(option.id);
            return (
              <DropdownMenuCheckboxItem
                key={option.id}
                checked={checked}
                onCheckedChange={(nextChecked) => {
                  const nextValues =
                    nextChecked === true
                      ? [...new Set([...selectedArrayValues, option.id])]
                      : selectedArrayValues.filter(
                          (item) => item !== option.id,
                        );
                  onChange(serializeArrayValue(nextValues));
                }}
                onSelect={(event) => event.preventDefault()}
              >
                {option.name}
              </DropdownMenuCheckboxItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : field.type === "boolean" ? (
      <label className="flex h-9 items-center gap-2 text-sm">
        <Checkbox
          id={id}
          checked={value === "true"}
          onCheckedChange={(checked) =>
            onChange(checked === true ? "true" : "false")
          }
        />
        Enabled
      </label>
    ) : field.type === "enum" && options.length > 0 ? (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className={commonClassName}>
          <SelectValue placeholder="Select value" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : field.type === "text" || field.type === "json" ? (
      <Textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={commonClassName}
        placeholder={`Enter ${field.variableNameFrontend ?? field.key}`}
        rows={3}
      />
    ) : (
      <Input
        id={id}
        type={
          field.type === "number" || field.type === "integer"
            ? "number"
            : field.type === "date"
              ? "date"
              : "text"
        }
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={commonClassName}
        placeholder={`Enter ${field.variableNameFrontend ?? field.key}`}
      />
    );

  return (
    <div className="grid gap-2">
      {input}
      {shouldLoadChildren && childFields.length > 0 && (
        <div className="ml-4 grid gap-2 rounded-md border border-dashed p-2">
          <div className="text-xs font-medium text-muted-foreground">
            {field.variableNameFrontend ?? field.key} settings
          </div>
          {childFields.map((childField) => (
            <div key={fieldValueKey(childField)} className="grid gap-1">
              <div className="text-sm font-medium">
                {childField.variableNameFrontend ?? childField.key}
              </div>
              <RuntimeVariableInput
                id={`${id}-${fieldValueKey(childField)}`}
                field={childField}
                value={getRuntimeChildFieldValue(childField, childValues)}
                analysisId={analysisId}
                linkedAnalyses={linkedAnalyses}
                linkedAnalysisSteps={linkedAnalysisSteps}
                excludedLinkedAnalysisStepIds={excludedLinkedAnalysisStepIds}
                linkedAnalysesLoading={linkedAnalysesLoading}
                linkedAnalysisStepsLoading={linkedAnalysisStepsLoading}
                onChange={(nextValue) => onChildChange?.(childField, nextValue)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
